/**
 * Enter your phone number.
 *
 * Built to docs/references/06-phone-number.png.
 *
 * The reference is a phone screenshot, so the portrait layout follows it
 * closely. There is no desktop reference for this screen, so the laptop
 * layout borrows the card treatment from the web login reference we do have
 * (03-web-login.png): a centred card on the tinted page, same radius, same
 * border. That keeps the two web entry screens looking like one product.
 *
 * The number is assembled and normalised here rather than in the service,
 * because the service takes E.164 and nothing else — one spelling per
 * person, or the same human ends up with two accounts.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { COUNTRIES_BY_NAME, countryByCode } from '@zivapp/shared/countries'
import { toE164, isValidE164, userMessage } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import ThemeToggle from '../components/ui/ThemeToggle.jsx'
import './Login.css'

const DEFAULT_CODE = 'NG'

export default function Login () {
  const navigate = useNavigate()
  const { requestCode } = useAuth()

  const [countryCode, setCountryCode] = useState(DEFAULT_CODE)
  const [national, setNational] = useState('')
  const [isSending, setSending] = useState(false)
  const [error, setError] = useState(null)

  const country = countryByCode(countryCode)

  // Normalising as the user types means the Next button reflects what will
  // actually be submitted, not what is on screen.
  const e164 = useMemo(() => {
    if (!national.trim() || !country) return null
    try {
      return toE164(national, country.dial)
    } catch {
      return null
    }
  }, [national, country])

  const canSubmit = Boolean(e164 && isValidE164(e164)) && !isSending

  async function onSubmit (event) {
    event.preventDefault()
    if (!canSubmit) return

    setSending(true)
    setError(null)
    try {
      // devCode is only ever present while the server has no SMS provider.
      // Carried through so the verify screen can show it, which is the
      // difference between the app being usable and needing a terminal.
      const { verificationId, devCode } = await requestCode(e164)
      navigate('/verify', { state: { phone: e164, verificationId, devCode } })
    } catch (err) {
      setError(userMessage(err))
      setSending(false)
    }
  }

  return (
    <main className="lg">
      <ThemeToggle className="lg__theme" />

      <form className="lg__card" onSubmit={onSubmit} noValidate>
        <h1 className="lg__title">Enter your phone number</h1>

        <p className="lg__sub">
          ZIvapp will need to verify your phone number.
          {' '}
          <a className="lg__what" href="/help/my-number">What’s my number?</a>
        </p>

        <div className="lg__field">
          <select
            className="lg__country"
            value={countryCode}
            onChange={e => setCountryCode(e.target.value)}
            aria-label="Country"
          >
            {COUNTRIES_BY_NAME.map(c => (
              <option key={c.code} value={c.code}>
                {c.flag}  {c.name}
              </option>
            ))}
          </select>
          <svg className="lg__caret" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div className="lg__row">
          <span className="lg__plus" aria-hidden="true">+</span>

          <label className="lg__dial-wrap">
            <span className="ziv-sr-only">Country calling code</span>
            <input
              className="lg__dial"
              value={country?.dial ?? ''}
              readOnly
              tabIndex={-1}
              aria-readonly="true"
            />
          </label>

          <label className="lg__number-wrap">
            <span className="ziv-sr-only">Phone number</span>
            <input
              className="lg__number"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="phone number"
              value={national}
              onChange={e => setNational(e.target.value)}
              autoFocus
            />
          </label>
        </div>

        <p className="lg__carrier">Carrier charges may apply</p>

        {error && <p className="lg__error" role="alert">{error}</p>}

        <button type="submit" className="lg__next" disabled={!canSubmit}>
          {isSending ? 'Sending…' : 'Next'}
        </button>
      </form>
    </main>
  )
}
